import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import Button from "../../Generic/Button";
import Card from "../../Generic/Card";
import Dropdown from "../../Generic/Dropdown";
import InputBox from "../../Generic/InputBox";
import SuccessButton from "../../Generic/SuccessButton";
import Textarea from "../../Generic/Textarea";
import { errorNotification, successNotification } from "../../Generic/Notifications";
import StudioService from "../Services/StudioService";
import { AppRoutes, DashboardModules, StudioSubModules } from "../../config/app.config";
import {
	DISH_DESCRIPTION_MAX_LENGTH,
	DISH_TITLE_MAX_LENGTH,
} from "./constants";
import { useDish } from "./context/DishContext";
import { Cross } from "../../Generic/Symbols";
import { buildDishTagDropdownOptions, dishTagLabel, isDishTag, validateDishForm } from "./utils";
import { zIndex } from "../../config/zIndex";

const Page = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1.5rem;
`;

const DishCreateCard = styled(Card)`
	width: 100%;
	align-items: center;
	flex-direction: column;
`;

const DishCreateCardHeader = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	width: 100%;
	padding-bottom: 1.5rem;
`;

const DishCreateCardHeaderTitle = styled.div`
	font-size: 2.3rem;
	font-weight: 600;
	color: #000;
`;

const DishCreateForm = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	width: 100%;
	align-items: center;
`;

const DishCreateFormItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	width: 100%;
`;

const DishCreateFormItemLabel = styled.div`
	font-size: 1rem;
	font-weight: 600;
	color: #000;
`;

const DishCreateFormItemInput = styled(InputBox)`
	width: 30rem;
`;

const DishCreateFormItemTextarea = styled(Textarea)`
	width: 100%;
`;

const DishCreateFormFooter = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 1rem;
	padding: 1rem 0;
`;

const DishCreateFormItemTags = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: flex-start;
	width: 100%;
`;

const DishCreateFormItemTag = styled.div`
	position: relative;
	margin: 0 0.5rem 0 0;
	padding: 0.5rem 0 0 0;
`;

const DishCreateFormItemTagRemove = styled.div`
	position: absolute;
	top: 0;
	right: 0%;
	z-index: ${zIndex.stickyHeader};
	cursor: pointer;
`;

const DishCreateFormItemTagPill = styled.div`
	background: rgba(45, 35, 66, 0.8);
	border-radius: 3px 0 0 3px;
	color: #fff;
	display: inline-block;
	height: 26px;
	line-height: 26px;
	padding: 0 20px 0 23px;
	position: relative;
	margin: 0 10px 10px 0;
`;

const DishCreateFormItemTagInputRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
	width: 100%;
	margin-top: 0.25rem;
`;

const DishTagDropdownWrap = styled.div`
	flex: 1;
	min-width: 12rem;
	max-width: 24rem;
`;

const DishCreate = () => {
	const navigate = useNavigate();
	const { createDishDraft, updateCreateDishDraft, resetCreateDishDraft, invalidateDishListCache, setDishDetailsListPrefetch } = useDish();
	const [isDishCreateSubmitting, setIsDishCreateSubmitting] = useState(false);
	const [tagPickerKey, setTagPickerKey] = useState(0);

	const dishFormTitle = createDishDraft.title ?? "";
	const dishFormDescription = createDishDraft.description ?? "";
	const dishFormNotes = createDishDraft.notes ?? "";
	const dishFormImageUrl = createDishDraft.imageUrl ?? "";
	const dishFormTags = useMemo(() => {
		const raw = Array.isArray(createDishDraft.tags) ? createDishDraft.tags : [];
		return [...new Set(raw.filter(isDishTag))];
	}, [createDishDraft.tags]);
	const tagDropdownOptions = useMemo(() => buildDishTagDropdownOptions(dishFormTags), [dishFormTags]);

	const listPath = AppRoutes[DashboardModules.STUDIO][StudioSubModules.DISH].DISH_LIST.getPath();

	function handleDishTextFieldChange(fieldName, value) {
		updateCreateDishDraft({ [fieldName]: value });
	}

	function handleDishTagRemove(tagToRemove) {
		updateCreateDishDraft({
			tags: dishFormTags.filter((t) => t !== tagToRemove),
		});
	}

	function handleTagDropdownPick(key) {
		if (!key || !isDishTag(key) || dishFormTags.includes(key)) {
			return;
		}
		updateCreateDishDraft({
			tags: [...dishFormTags, key],
		});
		setTagPickerKey((k) => k + 1);
	}

	async function submitNewDish() {
		try {
			validateDishForm({
				title: dishFormTitle,
				description: dishFormDescription,
				notes: dishFormNotes,
				imageUrl: dishFormImageUrl,
				tags: dishFormTags,
			});
		} catch (error) {
			errorNotification(error.message);
			return;
		}

		setIsDishCreateSubmitting(true);
		const requestPayload = {
			title: dishFormTitle.trim(),
			description: dishFormDescription.trim(),
			notes: dishFormNotes.trim(),
			imageUrl: dishFormImageUrl.trim(),
			tags: dishFormTags.filter(isDishTag),
		};
		const response = await StudioService.createDish(requestPayload);
		const { data, error } = response;
		setIsDishCreateSubmitting(false);
		if (error) {
			errorNotification(error || "Could not create dish");
			return;
		}
		if (data?._id) {
			successNotification("Dish created successfully");
			resetCreateDishDraft();
			invalidateDishListCache();
			setDishDetailsListPrefetch({ dishId: data._id, title: dishFormTitle.trim() });
			const detailsPath = AppRoutes[DashboardModules.STUDIO][StudioSubModules.DISH].DISH_DETAILS.getPath(data._id);
			navigate(detailsPath, { replace: true });
		} else {
			errorNotification("Create succeeded but no dish id was returned");
		}
	}

	function handleNavigateToDishList() {
		navigate(listPath);
	}

	return (
		<Page>
			<DishCreateCardHeader>
				<DishCreateCardHeaderTitle>Create Dish</DishCreateCardHeaderTitle>
			</DishCreateCardHeader>
			<DishCreateCard>
				<DishCreateForm>
					<DishCreateFormItem>
						<DishCreateFormItemLabel>Name</DishCreateFormItemLabel>
						<DishCreateFormItemInput
							maxLength={DISH_TITLE_MAX_LENGTH}
							value={dishFormTitle}
							onChange={(event) => handleDishTextFieldChange("title", event.target.value)}
						/>
					</DishCreateFormItem>
					<DishCreateFormItem>
						<DishCreateFormItemLabel>Tags (at least one)</DishCreateFormItemLabel>
						<DishCreateFormItemTags>
							{dishFormTags.map((tag, index) => (
								<DishCreateFormItemTag key={`${tag}-${index}`}>
									<DishCreateFormItemTagRemove onClick={() => handleDishTagRemove(tag)} aria-label={`Remove tag ${dishTagLabel(tag)}`}>
										<Cross width="15px" />
									</DishCreateFormItemTagRemove>
									<DishCreateFormItemTagPill id={tag}>{dishTagLabel(tag)}</DishCreateFormItemTagPill>
								</DishCreateFormItemTag>
							))}
						</DishCreateFormItemTags>
						<DishCreateFormItemTagInputRow>
							{tagDropdownOptions.length > 0 ? (
								<DishTagDropdownWrap>
									<Dropdown
										key={tagPickerKey}
										head="Add tag…"
										value={undefined}
										optionsList={tagDropdownOptions}
										onChange={handleTagDropdownPick}
										padding="0.5rem"
										headerFontSize="1rem"
										boxShadow=""
										border="1px solid rgba(0,0,0,0.6)"
										margin="0"
										widthVal="100%"
									/>
								</DishTagDropdownWrap>
							) : (
								<span style={{ color: "rgba(0,0,0,0.55)", fontSize: "0.95rem" }}>All tags added</span>
							)}
						</DishCreateFormItemTagInputRow>
					</DishCreateFormItem>
					<DishCreateFormItem>
						<DishCreateFormItemLabel>Description (max {DISH_DESCRIPTION_MAX_LENGTH} characters)</DishCreateFormItemLabel>
						<DishCreateFormItemTextarea
							maxLength={DISH_DESCRIPTION_MAX_LENGTH}
							height="8rem"
							value={dishFormDescription}
							onChange={(event) => handleDishTextFieldChange("description", event.target.value)}
						/>
					</DishCreateFormItem>
					<DishCreateFormItem>
						<DishCreateFormItemLabel>Notes</DishCreateFormItemLabel>
						<DishCreateFormItemTextarea value={dishFormNotes} onChange={(event) => handleDishTextFieldChange("notes", event.target.value)} />
					</DishCreateFormItem>
					<DishCreateFormItem>
						<DishCreateFormItemLabel>Image URL</DishCreateFormItemLabel>
						<DishCreateFormItemInput value={dishFormImageUrl} onChange={(event) => handleDishTextFieldChange("imageUrl", event.target.value)} />
					</DishCreateFormItem>
				</DishCreateForm>
				<DishCreateFormFooter>
					<Button onClick={handleNavigateToDishList}>Cancel</Button>
					<SuccessButton onClick={submitNewDish} disabled={isDishCreateSubmitting}>
						{isDishCreateSubmitting ? "Creating…" : "Create dish"}
					</SuccessButton>
				</DishCreateFormFooter>
			</DishCreateCard>
		</Page>
	);
};

export default DishCreate;
